/**
 * Role Switcher (navbar dropdown)
 * Shows "Client Mode / Professional Mode" pill when the user has both roles.
 * Falls back to "Add Client/Professional Profile" button when they only have one.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, User as UserIcon, Briefcase, Plus, CheckCircle2 } from "lucide-react";
import { useAuth, useLanguage, API } from "@/context/AppContext";

export const RoleSwitcher = () => {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [roles, setRoles] = useState(null);
  const [switching, setSwitching] = useState(false);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/account/roles`, { withCredentials: true });
      setRoles(res.data);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };

  useEffect(() => {
    if (user) fetchRoles();
  }, [user?.user_id]);

  const switchTo = async (target) => {
    if (target === roles?.active_role) return;
    setSwitching(true);
    try {
      await axios.post(`${API}/account/switch-role`, { role: target }, { withCredentials: true });
      // Refresh both /auth/me and /account/roles
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(meRes.data);
      await fetchRoles();
      toast.success(target === "provider" ? (t("switchedToExpert") || "Switched to Expert mode") : (t("switchedToClient") || "Switched to Client mode"));
      // Always land on the role's home dashboard
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not switch role");
    } finally {
      setSwitching(false);
    }
  };

  if (!user || !roles) return null;

  const activatedRoles = roles.roles || [];
  const activeRole = roles.active_role;
  const hasClient = activatedRoles.includes("client");
  const hasProvider = activatedRoles.includes("provider");

  // Only one role → show an "Add other role" button
  if (activatedRoles.length <= 1) {
    const missing = hasClient ? "provider" : "client";
    return (
      <Button
        variant="outline"
        size="sm"
        className="border-[#0044CC] text-[#0044CC] hover:bg-[#0044CC] hover:text-white"
        onClick={() => navigate(`/dashboard/add-role?role=${missing}`)}
        data-testid="add-role-cta"
      >
        <Plus className="w-4 h-4 mr-1" />
        {missing === "provider" ? (t("addExpertProfile") || "Add Expert Profile") : (t("addClientProfile") || "Add Client Profile")}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={switching} data-testid="role-switcher-trigger">
          {activeRole === "provider" ? (
            <Briefcase className="w-4 h-4 mr-1.5" />
          ) : (
            <UserIcon className="w-4 h-4 mr-1.5" />
          )}
          <span className="hidden sm:inline">
            {activeRole === "provider" ? (t("expertMode") || "Expert Mode") : (t("clientMode") || "Client Mode")}
          </span>
          <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t("switchRole") || "Switch role"}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => switchTo("client")}
          className="cursor-pointer"
          data-testid="role-switcher-item-client"
        >
          <UserIcon className="w-4 h-4 mr-2" />
          <span className="flex-1">{t("clientMode") || "Client Mode"}</span>
          {activeRole === "client" && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => switchTo("provider")}
          className="cursor-pointer"
          data-testid="role-switcher-item-provider"
        >
          <Briefcase className="w-4 h-4 mr-2" />
          <span className="flex-1">{t("expertMode") || "Expert Mode"}</span>
          {activeRole === "provider" && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RoleSwitcher;
